import { ElMessage, ElMessageBox } from 'element-plus'
import { bindWechat } from '@/api/auth'
import { getUserInfo } from '@/api/auth'
import { getToken, setUserInfo } from '@/utils/auth'
import {
  isWechatBrowser,
  redirectWeChatOAuth,
  WECHAT_BOUND_KEY,
  isWechatOAuthSupported,
  getUrlCode,
  getUrlState,
  cleanWechatCodeFromUrl
} from '@/utils/wechatSilentAuth'

export { WECHAT_BOUND_KEY }
export const BIND_WECHAT_STATE = 'bind_wechat'
export const PENDING_BIND_WECHAT_KEY = 'deepark_pending_bind_wechat'
export const PENDING_BIND_PHONE_KEY = 'deepark_pending_bind_phone'
export const BIND_PHONE_PROMPT_EVENT = 'deepark:bind-phone-prompt'
const OAUTH_CODE_KEY = 'deepark_wx_oauth_code'

/** 从 getInfo 响应中取出 sys_user */
export function extractSysUser(info) {
  if (!info) return null
  return info.user || info.data?.user || info
}

export function hasBoundWechat(userInfo) {
  const user = extractSysUser(userInfo)
  const code = user?.wechatCode || user?.wechat_code
  return !!(code && String(code).trim())
}

export function hasBoundPhone(userInfo) {
  const user = extractSysUser(userInfo)
  const phone = user?.phonenumber || user?.phone
  return !!(phone && /^1[3-9]\d{9}$/.test(String(phone).trim()))
}

export function syncWechatBoundFlag(userInfo) {
  const user = extractSysUser(userInfo)
  const code = user?.wechatCode || user?.wechat_code
  if (code && String(code).trim()) {
    localStorage.setItem(WECHAT_BOUND_KEY, '1')
  } else {
    localStorage.removeItem(WECHAT_BOUND_KEY)
  }
}

export function isWechatBoundFlagSet() {
  return localStorage.getItem(WECHAT_BOUND_KEY) === '1'
}

export function clearWechatBoundFlag() {
  localStorage.removeItem(WECHAT_BOUND_KEY)
}

/** 登录成功后 OAuth 回调地址（默认首页，保留 redirect 目标路径） */
export function buildPostLoginOAuthRedirectUrl() {
  const origin = window.location.origin
  try {
    const params = new URLSearchParams(window.location.search)
    const redirect = params.get('redirect')
    if (redirect) {
      let target = Array.isArray(redirect) ? redirect[0] : String(redirect)
      try {
        target = decodeURIComponent(target)
      } catch (e) { /* keep */ }
      if (target.startsWith('/') && !target.startsWith('//') && !target.startsWith('/login')) {
        return origin + target.split('?')[0]
      }
    }
  } catch (e) { /* ignore */ }
  return origin + '/'
}

/** 登录成功：标记进入首页后需弹绑定提示（先跳转，后弹框） */
export function markPendingBindPrompt(userInfo, loginMethod) {
  syncWechatBoundFlag(userInfo)
  if (loginMethod === 'phone' && !hasBoundWechat(userInfo) && isWechatBrowser() && isWechatOAuthSupported()) {
    sessionStorage.setItem(PENDING_BIND_WECHAT_KEY, '1')
  }
  if (loginMethod === 'wechat' && !hasBoundPhone(userInfo)) {
    sessionStorage.setItem(PENDING_BIND_PHONE_KEY, '1')
  }
}

export function consumePendingBindWechat() {
  const pending = sessionStorage.getItem(PENDING_BIND_WECHAT_KEY) === '1'
  if (pending) {
    sessionStorage.removeItem(PENDING_BIND_WECHAT_KEY)
  }
  return pending
}

export function consumePendingBindPhone() {
  const pending = sessionStorage.getItem(PENDING_BIND_PHONE_KEY) === '1'
  if (pending) {
    sessionStorage.removeItem(PENDING_BIND_PHONE_KEY)
  }
  return pending
}

/** 发起微信绑定 OAuth（redirectPathOrUrl：路径如 /user/profile/details，或完整 URL） */
export function startBindWechatOAuth(redirectPathOrUrl) {
  if (!isWechatBrowser()) {
    ElMessage.warning('请在微信内打开后进行绑定')
    return false
  }
  if (!isWechatOAuthSupported()) {
    ElMessage.warning('当前环境不支持微信绑定')
    return false
  }
  const origin = window.location.origin
  let redirectUrl = redirectPathOrUrl
  if (!redirectUrl || !redirectUrl.startsWith('http')) {
    let path = redirectUrl || '/'
    if (!path.startsWith('/')) {
      path = '/' + path
    }
    redirectUrl = origin + path
  }
  redirectWeChatOAuth({
    state: BIND_WECHAT_STATE,
    redirectUrl
  })
  return true
}

/** 首页弹框：绑定微信（可取消） */
export async function promptBindWechatDialog() {
  try {
    await ElMessageBox.confirm(
      '绑定微信后，下次可在微信内免登录，并使用微信支付。',
      '绑定微信',
      {
        confirmButtonText: '去绑定',
        cancelButtonText: '暂不绑定',
        type: 'info'
      }
    )
    startBindWechatOAuth(buildPostLoginOAuthRedirectUrl())
  } catch (e) {
    /* 用户跳过 */
  }
}

/** 登录跳转完成后，消费待绑定标记并弹框（由 Login 在 navigate 后主动调用） */
export async function runPendingBindPrompts() {
  if (consumePendingBindWechat()) {
    await promptBindWechatDialog()
  }
  if (consumePendingBindPhone()) {
    window.dispatchEvent(new CustomEvent(BIND_PHONE_PROMPT_EVENT))
  }
}

/** OAuth 回调：已登录用户绑定微信（在首页等任意页处理） */
export async function processWechatBindOAuthReturn() {
  if (!getToken()) {
    return false
  }
  const code = getUrlCode('code')
  const state = getUrlState()
  if (!code || state !== BIND_WECHAT_STATE) {
    return false
  }
  if (sessionStorage.getItem(OAUTH_CODE_KEY) === code) {
    cleanWechatCodeFromUrl()
    return false
  }
  sessionStorage.setItem(OAUTH_CODE_KEY, code)
  try {
    await bindWechat(code)
    sessionStorage.removeItem(OAUTH_CODE_KEY)
    cleanWechatCodeFromUrl()
    const userInfo = await getUserInfo()
    setUserInfo(userInfo)
    syncWechatBoundFlag(userInfo)
    ElMessage.success('微信绑定成功')
    return true
  } catch (error) {
    sessionStorage.removeItem(OAUTH_CODE_KEY)
    cleanWechatCodeFromUrl()
    ElMessage.error(error.message || '绑定微信失败')
    return false
  }
}
